import { useState } from 'react';
import * as Yup from "yup";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { classNames } from "primereact/utils";
import { useDebounce } from "primereact/hooks";
import { InputText } from "primereact/inputtext";
import { RadioButton } from "primereact/radiobutton";
import { useQuery } from "@tanstack/react-query";

import { useForm, Controller, FieldValues } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import FormWrapper15620 from "./Form15620/FormWrapper15620";

import Loading from "../../components/Loading/Loading";
import PublicHeader from "../../components/PublicHeader/PublicHeader";
import DocumentGeneration from "../../components/DocumentGeneration/DocumentGeneration";
import { getYupDynamicScheme } from "../../components/DocumentGeneration/helpers/yupValidationHelpers";

import { TemplatesService } from "./TemplateService";


function getFormDataFromStorage() {

    const formData = sessionStorage.getItem('processing');
    return formData ? JSON.parse(formData) : null;
}

const PublicFile83b = () => {
    const [inputValue, setInputValue] = useState('');
    const [isSupposeExists, setSuppose] = useState(false);

    const [isRegularForm, setRegularForm] = useState(() => {
        const formData = getFormDataFromStorage();
        return formData ? formData.isRegular : true;
    });
    const [isFormTypeSelected, setFormTypeSelected] = useState(() => {
        const formData = getFormDataFromStorage();
        return Boolean(formData);
    });

    const [showChooseFormTypeDialog, setShowChooseFormTypeDialog] = useState(!isFormTypeSelected);

    const {
        reset,
        control,
        formState: { errors },
        handleSubmit,
    } = useForm({
        defaultValues: { isRegular: true, email: '' },
        resolver: yupResolver(Yup.object().shape({
            isRegular: Yup.boolean(),
            email: Yup.string().when("isRegular", {
                is: (val: boolean) => val === true,
                then: (scheme) => scheme.notRequired(),
                otherwise: Yup.string().email("Invalid email address. E.g. example@email.com").required("Email is required."),
            })
        })),
    });

    const { data: initialInfo } = useQuery({
        queryKey: TemplatesService.fetchInitialInfo.queryKey,
        queryFn: () => TemplatesService.fetchInitialInfo.queryFn(),
    });
    const { templateCode, templateSpouseCode, price, uspsPrice, templateCrypto } = initialInfo || {};
    const template = isSupposeExists
        ? templateSpouseCode
        : templateCode;

    const { data: fieldsConfiguration, isLoading: requiredFieldsLoading } = useQuery({
        queryKey: [...TemplatesService.fetchTemplateConfiguration.queryKey, isSupposeExists],
        queryFn: () => TemplatesService.fetchTemplateConfiguration.queryFn(template),
        enabled: Boolean(templateCode) && Boolean(templateSpouseCode),
        gcTime: 0,
    });

    const { data: docAsHtml, isLoading: docAsHtmlLoading } = useQuery({
        queryKey: [...TemplatesService.exportDocAsHtml.queryKey, isSupposeExists],
        queryFn: () => TemplatesService.exportDocAsHtml.queryFn(template),
        enabled: Boolean(templateCode) && Boolean(templateSpouseCode),
        gcTime: 0,
    });
    const formSchema = fieldsConfiguration?.configs ? getYupDynamicScheme(fieldsConfiguration?.configs) : undefined;

    return (
        <div className="h-full max-w-full surface-100 flex flex-column">
            <Dialog visible={showChooseFormTypeDialog}
                onHide={() => {
                    setRegularForm(true);
                    setFormTypeSelected(true);
                    setShowChooseFormTypeDialog(false);
                }
                }
                style={{ width: '25vw' }}
                header="Which form would you like to use?">
                <div className="h-full flex flex-column gap-3">
                    <form className="h-full p-fluid flex flex-column gap-3" onSubmit={handleSubmit((formData:FieldValues) => {
                        setInputValue(formData.email);
                        setFormTypeSelected(true);
                        setShowChooseFormTypeDialog(false);
                        reset()
                    })} >
                        <Controller
                            name="isRegular"
                            control={control}
                            render={({ field }) => {
                                return (
                                    <>
                                        <div className="w-full flex align-items-start pt-1">
                                            <RadioButton
                                               name={field.name}
                                                value={true}
                                                checked={field.value === true}
                                                onChange={(e) => {
                                                    field.onChange(e.value);
                                                    setRegularForm(true)
                                                }}
                                            />
                                            <div className="ml-2 flex flex-column gap-1">
                                                <span className="font-bold text-lg">Freeform statement</span>
                                                <span className="font-italic text-base">Convenient for restricted stock awards</span>
                                            </div>
                                        </div>
                                        <div className="w-full flex align-items-start pt-1">
                                            <RadioButton
                                                value={false}
                                                name={field.name}
                                                checked={field.value === false}
                                                onChange={(e) => {
                                                    field.onChange(e.value);
                                                    setRegularForm(false);
                                                }}
                                            />
                                            <div className="ml-2 flex flex-column gap-1">
                                                <span className="font-bold text-lg">Form 15620</span>
                                                <span className="font-italic text-base">Convenient for profits interests, tokens, and other equity/property types</span>
                                            </div>
                                        </div>
                                    </>
                                )
                            }} />



                        {
                            !isRegularForm ? (
                                <div className="w-full flex flex-column align-items-start pt-1 gap-1">
                                    <div className="my-2 px-1 font-medium">
                                        Please enter your email address. We will use this
                                        address to send you your completed document for e-signature.
                                        Please check for any typos.
                                    </div>
                                    <label htmlFor="email" className={classNames({ "text-red-500": errors.email })}>
                                        Email<span className="ml-1 text-red-300">*</span>
                                    </label>
                                    <Controller
                                        name="email"
                                        control={control}
                                        render={({ field, fieldState }) => {
                                            return (
                                                <>
                                                    <InputText  {...field}
                                                        id="email"
                                                        tooltip="Enter the email"
                                                        tooltipOptions={{ position: "top", style: { maxWidth: "30vw" } }}
                                                        onChange={(e) => {
                                                            field.onChange(e.target.value)
                                                        }}
                                                        className={classNames({
                                                            'p-invalid': fieldState.invalid,
                                                            'text-red-500': fieldState.invalid,
                                                        })} />
                                                    {
                                                        fieldState.invalid ? (
                                                            <small className="text-red-500">
                                                                {fieldState.error?.message}
                                                            </small>
                                                        ) : null
                                                    }

                                                </>
                                            )
                                        }}
                                    />

                                </div>
                            ) : null
                        }
                        <Button label="Select" type="submit" className="align-self-end w-auto" />
                    </form>
                </div>
            </Dialog>
            <PublicHeader />
            {
                isFormTypeSelected ? (
                    isRegularForm ? (
                        <>
                            <DocumentGeneration
                                isSupposeExists={isSupposeExists}
                                setSuppose={setSuppose}
                                template={template}
                                data={fieldsConfiguration}
                                requiredFieldsLoading={requiredFieldsLoading}
                                docAsHtml={docAsHtml}
                                docAsHtmlLoading={docAsHtmlLoading}
                                isPublic
                                price={price}
                                uspsPrice={uspsPrice}
                                showSelectTemplate={Boolean(templateSpouseCode)}
                                formSchema={formSchema}
                            />
                        </>
                    ) : (
                        <FormWrapper15620

                            price={price}
                            uspsPrice={uspsPrice}
                            isPublic={true}
                            email={inputValue}
                            isDataLoaded={true}
                            templateId={templateCrypto}
                            isPublicOpenAi={false}
                            paymentEnabled={true} deliveryService={''} />
                    )
                ) : (
                    <div className="w-full h-full flex align-items-center justify-content-between">
                        <Loading />
                    </div>
                )
            }

        </div>
    );

}

export default PublicFile83b;
